import { createContext, useContext } from 'react';
import type { StoreApi } from 'zustand';
import { create } from 'zustand';
import type { NodeContact } from '@hub/models/api/node-contact.types';

export interface BearingHubStoreData {
  contact: NodeContact | null;
}

export interface BearingHubPageStore extends BearingHubStoreData {
  setContact: (contact: NodeContact | null) => void;
}

export const createBearingHubStore = (
  set: StoreApi<BearingHubPageStore>['setState'],
  get: StoreApi<BearingHubPageStore>['getState'],
  initialData: Partial<BearingHubStoreData>
) => ({
  contact: initialData.contact ?? null,
  setContact: (contact: NodeContact | null) => set(state => ({ ...state, contact })),
});

export const createStore = (initialData: Partial<BearingHubStoreData>) =>
  create<BearingHubPageStore>((set, get) => createBearingHubStore(set, get, initialData));

const StoreContext = createContext<StoreApi<BearingHubPageStore> | null>(null);

export const BearingHubPageStoreProvider = ({
  initialData,
  children,
}: {
  initialData: Partial<BearingHubStoreData>;
  children?: React.ReactNode;
}) => {
  const store = createStore(initialData);
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
};

export const useBearingHubPageStore = <T,>(selector: (state: BearingHubPageStore) => T): T => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('Store not found');
  }
  return selector(store.getState());
};
